import { useMutation } from '@apollo/client/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormMessage } from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_RESET_PASSWORD } from '~/gql/auth/auth'

function NewPassword() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const email = searchParams.get('email')

  useEffect(() => {
    if (!token || !email) {
      navigate('/')
    }
  }, [token, email, navigate])

  const resetSchema = z.object({
    password: z.string().min(8, { message: t('input.password') }),
    confirmPassword: z.string(),
  }).refine(data => data.password === data.confirmPassword, {
    message: t('password.not-match.message'),
    path: ['confirmPassword'],
  })

  type ResetFormValues = z.infer<typeof resetSchema>

  const form = useForm<ResetFormValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })

  const [resetPassword, { loading }] = useMutation(GQL_RESET_PASSWORD)

  const onSubmit = async (values: ResetFormValues) => {
    if (!token || !email) return

    const tokenString = token as string
    const emailString = email as string

    try {
      const { data } = await resetPassword({
        variables: {
          email: emailString,
          token: tokenString,
          password: values.password,
        },
      })
      if (data && data.resetPassword) {
        toast.success(t('new.password.success'))
        setTimeout(() => {
          navigate('/')
        }, 2000)
      } else {
        toast.error(t('bad.reset.message'))
      }
    } catch {
      toast.error(t('bad.server-login.message'))
    }
  }

  if (loading) {
    return (
      <p className="text-center">
        {t('common.loading')}
        {' '}
        ...
      </p>
    )
  }

  if (!token || !email) {
    return null
  }

  return (
    <div className="flex w-screen min-h-dvh flex-col items-center justify-center p-5">
      <Card className="sm:w-md w-full">
        <CardHeader>
          <CardTitle className="text-2xl">{t('new.password')}</CardTitle>
          <CardDescription>
            {t('new.password.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <FormControl>
                      <div className="grid gap-2">
                        <Label htmlFor="password">{t('common.password')}</Label>
                        <Input
                          id="password"
                          type="password"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <FormControl>
                      <div className="grid gap-2">
                        <Label htmlFor="confirmPassword">{t('common.confirm-password')}</Label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full">
                <p className="text-background-foreground">{t('common.submit')}</p>
              </Button>

              <div className="mt-4 text-center text-sm">
                <a
                  onClick={() => navigate('/')}
                  className="underline underline-offset-4 cursor-pointer"
                >
                  {t('login.return-message')}
                </a>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}

export default NewPassword
